import {
  type CardCycleConfig,
  type PlainDate,
  type InstallmentPlan as PlanCalculable,
  billedInstallmentsCount,
  firstChargeableNumber,
  formatMoney,
  installmentAmounts,
  isPlainDate,
  todayIn,
} from '@paybae/shared'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { WarningIcon } from '@phosphor-icons/react'

import { ErrorState, Skeleton } from '@/components/Estado'
import {
  AvisoError,
  Campo,
  FormSheet,
  Guardar,
  Monto,
  type OpcionSegmento,
  Segmentos,
  Texto,
} from '@/components/Form'
import type { Category, CreditCard, InstallmentPlan, InstallmentPlanInput } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import {
  useActualizarCompraMeses,
  useCards,
  useCategories,
  useCrearCompraMeses,
  useHousehold,
  useMeses,
} from '@/lib/queries'
import { useSession } from '@/lib/session'
import { mensajeDeError } from '@/routes/Recurring'

/**
 * Alta y edición de una compra a meses.
 *
 * LA RAZÓN DE EXISTIR DEL FORMULARIO
 *
 * Nadie recuerda cuánto le cobran al mes por el refrigerador; lo que recuerda es
 * el precio y el plazo. Por eso, en cuanto hay precio y plazo, la pantalla
 * enseña **la mensualidad calculada, grande**, y debajo lo que quedará por
 * pagar. Todo lo demás del formulario existe para poder llegar a esas dos
 * cifras.
 *
 * EL CÁLCULO ES EL MISMO QUE EL DEL SERVIDOR
 *
 * La mensualidad y el número de la primera cobrable salen de `@paybae/shared`,
 * el mismo motor que corre en la API. No es una aproximación para la vista: es
 * la cuenta buena, ejecutada en el teléfono para que responda sin esperar la
 * red. El servidor la vuelve a hacer como autoridad al guardar, y si alguna vez
 * discreparan sería un fallo del motor, no de esta pantalla.
 */

/** Los plazos que ofrecen los bancos en México. El resto se escribe a mano. */
const PLAZOS = [3, 6, 9, 12, 18, 24] as const
const OTRO = 'otro'

const TIPOS: ReadonlyArray<OpcionSegmento<'msi' | 'mci'>> = [
  { value: 'msi', label: 'Sin intereses', detalle: 'MSI · pagas el precio' },
  { value: 'mci', label: 'Con intereses', detalle: 'MCI · pagas de más' },
]

/**
 * Orden de las categorías. Una compra a meses casi siempre es un gasto
 * variable grande —muebles, electrónica, un viaje—, así que ese grupo va
 * primero. Los ingresos no aparecen: diferir un ingreso no significa nada.
 */
const RANGO_GRUPO: Record<Category['group'], number> = {
  VARIABLE: 0,
  FIXED: 1,
  DEBT: 2,
  DESPENSA: 3,
  SAVINGS: 4,
  INCOME: 9,
}

export function MesesForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const compras = useMeses()

  const cerrar = (): void => {
    void navigate('/tarjetas/meses')
  }

  if (!id) return <Formulario inicial={null} onCerrar={cerrar} />

  // En edición se espera al registro. Sembrar el formulario con ceros y
  // rellenarlo al llegar la respuesta movería el precio bajo el dedo de quien
  // ya empezó a corregirlo.
  if (compras.isPending) {
    return (
      <Hoja titulo="Editar compra a meses" onCerrar={cerrar}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <Skeleton className="h-[15px] w-32" />
            <Skeleton className="h-12" />
          </div>
        ))}
      </Hoja>
    )
  }

  const actual = (compras.data?.plans ?? []).find((p) => p.id === id) ?? null

  if (compras.isError || !actual) {
    return (
      <Hoja titulo="Editar compra a meses" onCerrar={cerrar}>
        <ErrorState
          titulo="No encontramos esa compra"
          mensaje={
            compras.error instanceof Error
              ? compras.error.message
              : 'Puede que se haya cancelado desde otro dispositivo.'
          }
          onReintentar={() => void compras.refetch()}
        />
      </Hoja>
    )
  }

  return <Formulario key={actual.id} inicial={actual} onCerrar={cerrar} />
}

function Hoja({
  titulo,
  onCerrar,
  children,
}: {
  titulo: string
  onCerrar: () => void
  children: React.ReactNode
}) {
  return (
    <FormSheet tinte="lavender" titulo={titulo} onCerrar={onCerrar} accion={<Guardar onClick={onCerrar} disabled />}>
      {children}
    </FormSheet>
  )
}

function Formulario({
  inicial,
  onCerrar,
}: {
  inicial: InstallmentPlan | null
  onCerrar: () => void
}) {
  const { membership } = useSession()
  // La fecha sale del huso del hogar, no del reloj del navegador: a las 23:30 en
  // México un teléfono en UTC ya estaría en el día siguiente y la compra de hoy
  // se rechazaría por futura.
  const hoy = todayIn(membership.household.timezone)
  const veElHogar = membership.role === 'ADMIN' || membership.role === 'MEMBER'

  const tarjetas = useCards()
  const categorias = useCategories()
  const hogar = useHousehold()

  const crear = useCrearCompraMeses()
  const actualizar = useActualizarCompraMeses()

  const [nombre, setNombre] = useState(inicial?.name ?? '')
  const [categoriaId, setCategoriaId] = useState(inicial?.categoryId ?? '')
  const [montoCents, setMontoCents] = useState(inicial?.totalAmountCents ?? 0)
  const [tarjetaId, setTarjetaId] = useState(inicial?.creditCardId ?? '')
  const [meses, setMeses] = useState(inicial?.months ?? 12)
  const [plazoLibre, setPlazoLibre] = useState(
    inicial ? !PLAZOS.includes(inicial.months as (typeof PLAZOS)[number]) : false,
  )
  const [plazoTexto, setPlazoTexto] = useState(String(inicial?.months ?? 12))
  const [sinIntereses, setSinIntereses] = useState(inicial?.interestFree ?? true)
  const [mensualidadCents, setMensualidadCents] = useState(
    inicial && !inicial.interestFree ? inicial.monthlyAmountCents : 0,
  )
  const [pagadas, setPagadas] = useState(String(inicial?.monthsPaid ?? 0))
  const [compradaEl, setCompradaEl] = useState<string>(inicial?.purchasedOn ?? hoy)
  const [atribuido, setAtribuido] = useState(
    inicial?.attributedToMemberId ?? (veElHogar ? '' : membership.id),
  )
  const [verTodasCategorias, setVerTodasCategorias] = useState(false)
  const [tocado, setTocado] = useState(false)

  const todasLasCategorias = opcionesCategoria(categorias.data ?? [])
  const nombreCategoria =
    todasLasCategorias.find((o) => o.value === categoriaId)?.label ?? 'Sin categoría'

  const cargando = tarjetas.isPending || categorias.isPending || hogar.isPending

  const tarjetasVivas = (tarjetas.data ?? []).filter((c) => !c.archivedAt)
  const tarjeta =
    (tarjetas.data ?? []).find((c) => c.id === tarjetaId) ?? null
  const integrantes = hogar.data?.members ?? []

  const pagadasNumero = pagadas.trim() === '' ? Number.NaN : Number(pagadas)
  /** Lo mínimo que puede cobrar el banco en MCI: el precio entre los meses. */
  const mensualidadMinima = meses >= 2 ? Math.ceil(montoCents / meses) : 0

  /**
   * Cuántas mensualidades ya cobró la tarjeta, calculado por la app.
   *
   * Este número **no se le debería preguntar a nadie**: sale de la fecha de la
   * compra y del día de corte de la tarjeta, y la app tiene los dos. Antes el
   * campo nacía vacío y quien registraba una compra vieja tenía que contar
   * cortes a mano; equivocarse por uno corre el calendario un mes entero y hace
   * que la siguiente mensualidad se anuncie en el corte que no es. Pasó.
   *
   * Se ofrece como sugerencia y no se impone, porque hay dos casos que la app no
   * puede saber: quien pagó por adelantado lleva más de las que le cobraron, y
   * quien viene atrasado no ha pagado todas las facturadas.
   */
  const cobradas =
    tarjeta && isPlainDate(compradaEl) && meses >= 1
      ? billedInstallmentsCount(
          { purchasedOn: compradaEl as PlainDate, months: meses },
          hoy as PlainDate,
          {
            statementDay: tarjeta.statementDay,
            paymentTermDays: tarjeta.paymentTermDays,
            paymentTermType: tarjeta.paymentTermType,
            cutoffInclusive: tarjeta.cutoffInclusive,
          },
        )
      : null

  // La sugerencia se sigue aplicando mientras nadie haya tocado el campo. Así
  // cambiar la fecha de compra o la tarjeta la recalcula, y en cuanto la persona
  // escribe algo distinto, manda ella.
  const [pagadasTocado, setPagadasTocado] = useState(false)
  useEffect(() => {
    if (pagadasTocado || inicial || cobradas === null) return
    setPagadas(String(cobradas))
  }, [cobradas, pagadasTocado, inicial])

  /** La persona declara un número distinto al que dicen los cortes. */
  const discrepa =
    cobradas !== null && Number.isInteger(pagadasNumero) && pagadasNumero !== cobradas

  const errores = {
    nombre:
      nombre.trim().length >= 2
        ? undefined
        : 'Ponle nombre a la compra: así la reconocerás dentro de un año.',
    monto: montoCents > 0 ? undefined : 'Escribe cuánto costó la compra.',
    tarjeta: tarjetaId !== '' ? undefined : 'Elige la tarjeta con la que compraste.',
    meses:
      Number.isInteger(meses) && meses >= 2 && meses <= 60
        ? undefined
        : 'El plazo va de 2 a 60 meses. A un mes no hay compra diferida, hay compra.',
    mensualidad: sinIntereses
      ? undefined
      : mensualidadCents <= 0
        ? 'Escribe la mensualidad que te cobra el banco.'
        : mensualidadCents * meses < montoCents
          ? `Con esa mensualidad el plan cobraría menos que la compra. La mínima a ${meses} meses es ${formatMoney(mensualidadMinima, { hideZeroCents: true })}.`
          : undefined,
    pagadas:
      Number.isInteger(pagadasNumero) && pagadasNumero >= 0 && pagadasNumero <= meses
        ? undefined
        : `De 0 a ${meses}: no puedes llevar más mensualidades pagadas que meses de plazo.`,
    fecha: !isPlainDate(compradaEl)
      ? 'Elige el día de la compra.'
      : compradaEl > hoy
        ? 'La compra no puede ser de una fecha futura.'
        : undefined,
  }
  const completo = Object.values(errores).every((e) => e === undefined)

  /**
   * La previsualización: mensualidad, lo que falta y los intereses.
   *
   * Se calcula con el motor compartido y con el ancla contable real —hoy al dar
   * de alta, el día en que se registró al editar—, así que dice exactamente lo
   * que el servidor va a guardar. Vale `null` mientras falte un dato: enseñar
   * una mensualidad a medio calcular es peor que no enseñar ninguna.
   */
  const calculo = useMemo(
    () =>
      calcular({
        montoCents,
        meses,
        sinIntereses,
        mensualidadCents,
        pagadas: pagadasNumero,
        compradaEl,
        anclaContable: inicial?.bookedOn ?? hoy,
        tarjeta,
      }),
    [montoCents, meses, sinIntereses, mensualidadCents, pagadasNumero, compradaEl, inicial, hoy, tarjeta],
  )

  const guardando = crear.isPending || actualizar.isPending
  const errorGuardado = crear.error ?? actualizar.error
  const fallo = crear.isError || actualizar.isError

  const elegirPlazo = (valor: string): void => {
    if (valor === OTRO) {
      setPlazoLibre(true)
      return
    }
    setPlazoLibre(false)
    setMeses(Number(valor))
    setPlazoTexto(valor)
  }

  const escribirPlazo = (texto: string): void => {
    setPlazoTexto(texto)
    const n = Number(texto)
    // Se acepta cualquier número mientras se escribe y la validación lo juzga
    // después: bloquear el teclado a mitad de «18» tras teclear el 1 haría
    // imposible escribir cualquier plazo de dos cifras.
    if (Number.isInteger(n)) setMeses(n)
  }

  const guardar = (): void => {
    setTocado(true)
    if (!completo || guardando) return
    if (!isPlainDate(compradaEl)) return

    const comunes = {
      name: nombre.trim(),
      totalAmountCents: montoCents,
      months: meses,
      monthsPaid: pagadasNumero,
      purchasedOn: compradaEl,
      categoryId: categoriaId || null,
      // Cadena vacía significa fondo común, que el servidor espera como nulo.
      attributedToMemberId: atribuido || null,
      interestFree: sinIntereses,
      // **Solo en MCI.** Mandarla en una compra sin intereses no es un dato de
      // más: el servidor lo rechaza, y con razón —aceptarla dejaría a quien la
      // capturó creyendo que su plan cobra eso.
      ...(sinIntereses ? {} : { monthlyAmountCents: mensualidadCents }),
    }

    const opciones = {
      onSuccess: () => {
        haptic('success')
        onCerrar()
      },
      // Sin limpiar nada: conservar lo capturado es lo que hace que el segundo
      // intento tras un fallo de red no cueste ni un toque más.
      onError: () => haptic('error'),
    }

    if (inicial) actualizar.mutate({ id: inicial.id, ...comunes }, opciones)
    else crear.mutate({ creditCardId: tarjetaId, ...comunes } as InstallmentPlanInput, opciones)
  }

  if (cargando) {
    return (
      <Hoja titulo={inicial ? 'Editar compra a meses' : 'Nueva compra a meses'} onCerrar={onCerrar}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <Skeleton className="h-[15px] w-32" />
            <Skeleton className="h-12" />
          </div>
        ))}
      </Hoja>
    )
  }

  return (
    <FormSheet
      tinte="lavender"
      titulo={inicial ? 'Editar compra a meses' : 'Nueva compra a meses'}
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          {fallo ? <AvisoError mensaje={mensajeDeError(errorGuardado)} /> : null}
          <Guardar onClick={guardar} disabled={!completo} guardando={guardando}>
            {completo
              ? inicial
                ? 'Guardar cambios'
                : 'Registrar la compra'
              : queFalta(errores)}
          </Guardar>
        </div>
      }
    >
      <Campo label="¿Qué compraste?" error={tocado ? errores.nombre : undefined}>
        <Texto
          value={nombre}
          onChange={setNombre}
          placeholder="Refrigerador, laptop, vuelos…"
          maxLength={80}
          invalido={tocado && errores.nombre !== undefined}
        />
      </Campo>

      {/* La categoría va en el sitio donde la pidió Daniel, pero plegada.
          Desplegar veinte casillas aquí empujaría el precio —el único dato que
          nadie puede adivinar— por debajo de dos pantallas de desplazamiento, y
          una compra a meses casi nunca cambia de categoría respecto a lo que la
          app propone. Se abre de un toque y se vuelve a cerrar al elegir. */}
      <Campo label="Categoría" ayuda="Define en qué grupo suma esta compra dentro de tus gráficas.">
        {verTodasCategorias ? (
          <Segmentos
            opciones={todasLasCategorias}
            value={categoriaId}
            onChange={(v) => {
              setCategoriaId(v)
              setVerTodasCategorias(false)
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              haptic('light')
              setVerTodasCategorias(true)
            }}
            className="flex min-h-[52px] w-full items-center gap-3 rounded-card border border-line bg-surface px-3.5 py-2.5 text-left active:scale-[0.99]"
          >
            <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-ink">
              {nombreCategoria}
            </span>
            <span className="shrink-0 text-[13px] font-semibold text-primary-text">Cambiar</span>
          </button>
        )}
      </Campo>

      <Campo
        label="¿Cuánto costó?"
        ayuda="El precio del ticket, completo. La mensualidad la sacamos de aquí."
        error={tocado ? errores.monto : undefined}
      >
        <Monto cents={montoCents} onChange={setMontoCents} />
      </Campo>

      {/* La tarjeta no se puede mover en una compra ya registrada: cada tarjeta
          tiene su día de corte, y cambiarla movería la fecha de las doce
          mensualidades. El servidor lo impide; aquí se dice por qué. */}
      {inicial ? (
        <Campo label="Tarjeta" ayuda="Una compra vive en la tarjeta con la que se hizo: cambiarla movería el corte de todas sus mensualidades.">
          <p className="flex min-h-[52px] items-center gap-2 rounded-card border border-line bg-surface-sunken px-3.5 text-[15px] font-semibold text-ink">
            {inicial.card.name}
            {inicial.card.last4 ? (
              <span className="font-mono text-[13px] font-normal text-ink-faint">
                ·· {inicial.card.last4}
              </span>
            ) : null}
          </p>
        </Campo>
      ) : (
        <Campo
          label="¿Con qué tarjeta?"
          ayuda="De su día de corte salen las fechas de cada mensualidad."
          error={tocado ? errores.tarjeta : undefined}
        >
          {tarjetasVivas.length === 0 ? (
            <p className="rounded-card bg-surface-sunken px-3 py-2.5 text-[12px] leading-snug text-ink-soft">
              Todavía no tienes tarjetas activas. Da de alta la tarjeta con la que compraste y
              vuelve aquí.
            </p>
          ) : (
            <Segmentos
              opciones={opcionesTarjeta(tarjetasVivas)}
              value={tarjetaId}
              onChange={setTarjetaId}
            />
          )}
        </Campo>
      )}

      <Campo label="¿A cuántos meses?" error={tocado ? errores.meses : undefined}>
        <Segmentos
          opciones={[
            ...PLAZOS.map((p) => ({ value: String(p), label: `${p} meses` })),
            { value: OTRO, label: 'Otro plazo', detalle: 'De 2 a 60' },
          ]}
          value={plazoLibre ? OTRO : String(meses)}
          onChange={elegirPlazo}
          columnas={3}
        />
        {plazoLibre ? (
          <Texto
            value={plazoTexto}
            onChange={escribirPlazo}
            inputMode="numeric"
            maxLength={2}
            placeholder="36"
            invalido={tocado && errores.meses !== undefined}
          />
        ) : null}
      </Campo>

      <Campo
        label="¿Con intereses o sin intereses?"
        ayuda={
          sinIntereses
            ? 'A meses sin intereses pagas exactamente el precio: la mensualidad la calculamos nosotros.'
            : 'Con intereses la mensualidad la fija el banco y no se puede deducir del precio, así que hay que capturarla.'
        }
      >
        <Segmentos
          opciones={TIPOS}
          value={sinIntereses ? 'msi' : 'mci'}
          onChange={(v) => setSinIntereses(v === 'msi')}
        />
      </Campo>

      {/* El campo de la mensualidad no existe en MSI. No es que esté deshabilitado:
          es que ahí no hay nada que decidir, y dejarlo a la vista invitaría a
          escribir un número que el servidor va a rechazar. */}
      {!sinIntereses ? (
        <Campo
          label="¿De cuánto es la mensualidad?"
          ayuda={
            montoCents > 0 && meses >= 2
              ? `La que te cobra el banco, con sus intereses ya dentro. A ${meses} meses no puede ser menor que ${formatMoney(mensualidadMinima, { hideZeroCents: true })}.`
              : 'La que te cobra el banco, con sus intereses ya dentro.'
          }
          error={tocado ? errores.mensualidad : undefined}
        >
          <Monto cents={mensualidadCents} onChange={setMensualidadCents} />
        </Campo>
      ) : null}

      <Previsualizacion
        calculo={calculo}
        sinIntereses={sinIntereses}
        meses={meses}
        // La cuenta a la vista, no la regla: «18 × $100 son $1,800 y la compra
        // costó $45,000» se entiende sin releer, y además es la comprobación que
        // hace el servidor.
        motivo={
          !sinIntereses && mensualidadCents > 0 && meses >= 2 && mensualidadCents * meses < montoCents
            ? `Esa mensualidad no alcanza: ${meses} × ${formatMoney(mensualidadCents, { hideZeroCents: true })} son ${formatMoney(mensualidadCents * meses, { hideZeroCents: true })} y la compra costó ${formatMoney(montoCents, { hideZeroCents: true })}.`
            : null
        }
      />

      <Campo
        label="¿Cuántas mensualidades ya pagaste?"
        ayuda={
          cobradas === null
            ? 'Elige la tarjeta y la fecha de compra y lo calculamos por ti.'
            : cobradas === 0
              ? 'La compra es reciente: su primer corte todavía no llega, así que van cero.'
              : `Lo calculamos por ti: desde que la compraste han cortado ${cobradas} ${cobradas === 1 ? 'estado de cuenta' : 'estados de cuenta'}, así que el banco ya te cobró ${cobradas}. Cámbialo solo si pagaste por adelantado o vienes atrasado.`
        }
        error={tocado ? errores.pagadas : undefined}
      >
        <Texto
          value={pagadas}
          onChange={(v) => {
            setPagadasTocado(true)
            setPagadas(v)
          }}
          inputMode="numeric"
          maxLength={2}
          placeholder={cobradas === null ? '0' : String(cobradas)}
          invalido={tocado && errores.pagadas !== undefined}
        />
      </Campo>

      {/* Aviso de corrección, no error: el número puede ser correcto.
          Se explica la consecuencia en fechas —que es lo único que se nota— y se
          ofrece el ajuste de un toque, porque quien está aprendiendo no tiene por
          qué deducir que «uno de más» corre todo el calendario un mes. */}
      {discrepa && cobradas !== null ? (
        <div className="flex flex-col gap-2 rounded-card bg-warning-soft px-3.5 py-3">
          <p className="flex items-start gap-2 text-[12.5px] leading-snug text-ink">
            <WarningIcon size={15} className="mt-0.5 shrink-0 text-warning" />
            <span>
              Según los cortes de tu {tarjeta?.name ?? 'tarjeta'}, el banco ya te cobró{' '}
              <strong>{cobradas}</strong> {cobradas === 1 ? 'mensualidad' : 'mensualidades'}, y
              pusiste <strong>{pagadasNumero}</strong>.{' '}
              {pagadasNumero > cobradas
                ? 'Con una de más nos saltamos un corte y la siguiente mensualidad se anunciaría un mes tarde.'
                : 'Con una de menos te cobraríamos un corte que ya pagaste.'}
            </span>
          </p>
          <button
            type="button"
            onClick={() => {
              haptic('light')
              setPagadasTocado(true)
              setPagadas(String(cobradas))
            }}
            className="min-h-[40px] self-start rounded-pill bg-surface px-4 text-[13px] font-semibold text-ink active:scale-95"
          >
            Usar {cobradas}
          </button>
        </div>
      ) : null}

      <Campo
        label="¿Qué día la compraste?"
        ayuda="De aquí sale en qué corte cayó la compra y, con él, la fecha de cada mensualidad."
        error={tocado ? errores.fecha : undefined}
      >
        {/* Fecha como texto `AAAA-MM-DD`, que es lo que entrega el input nativo y
            lo que espera el servidor. Nunca un `Date`. */}
        <input
          type="date"
          value={compradaEl}
          max={hoy}
          onChange={(e) => setCompradaEl(e.target.value)}
          className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
        />
      </Campo>

      <Campo
        label="¿De quién es el gasto?"
        ayuda="Lo del fondo común se reparte entre el hogar; lo personal se descuenta solo de quien lo compró. La mensualidad la paga siempre quien lleva la tarjeta."
      >
        <Segmentos
          opciones={opcionesIntegrante(integrantes, veElHogar ? null : membership.id)}
          value={atribuido}
          onChange={setAtribuido}
        />
      </Campo>
    </FormSheet>
  )
}

/* ── La cifra que justifica el formulario ───────────────────────────────── */

interface Calculo {
  mensualidadCents: number
  /** En MSI el residuo va en la última, como lo cobra el banco. */
  ultimaCents: number
  quedan: number
  faltaCents: number
  totalAPagarCents: number
  interesesCents: number
  /** Mensualidades que ningún corte nuestro puede cobrar por ser anteriores al
      día en que la compra entró en los libros. */
  fueraDeLibros: number
}

function Previsualizacion({
  calculo,
  sinIntereses,
  meses,
  motivo,
}: {
  calculo: Calculo | null
  sinIntereses: boolean
  meses: number
  /** Por qué no hay cifra, cuando la razón es concreta y no «falta capturar». */
  motivo: string | null
}) {
  if (!calculo) {
    return (
      <div className="rounded-card border border-dashed border-line px-3.5 py-5 text-center">
        <p
          className={cn(
            'text-[13px] leading-snug',
            motivo ? 'text-expense-text' : 'text-ink-soft',
          )}
        >
          {motivo ??
            (sinIntereses
              ? 'Escribe el precio y elige el plazo: aquí aparecerá tu mensualidad.'
              : 'Escribe el precio, el plazo y la mensualidad del banco: aquí verás cuánto son los intereses.')}
        </p>
      </div>
    )
  }

  const pctInteres =
    calculo.interesesCents > 0 && calculo.totalAPagarCents > calculo.interesesCents
      ? Math.round((calculo.interesesCents / (calculo.totalAPagarCents - calculo.interesesCents)) * 1000) / 10
      : 0

  return (
    <div className="rounded-card bg-primary-soft px-3.5 py-4">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-primary-text">
        Tu mensualidad
      </p>
      {/* La cifra en cuerpo de titular: es lo que se viene a averiguar. En tinta,
          no en el color de marca — un número grande de color se lee como aviso. */}
      <p
        data-money
        className="mt-1 font-display text-[38px] font-extrabold leading-none tracking-[-0.05em] text-ink"
      >
        {formatMoney(calculo.mensualidadCents)}
        <span className="font-sans text-[15px] font-semibold tracking-normal text-ink-soft">
          {' '}
          × {meses}
        </span>
      </p>

      <p data-money className="mt-2.5 text-[13px] leading-snug text-ink">
        {calculo.quedan === 0 ? (
          'Con lo que ya pagaste, esta compra queda saldada: no te cobrará ni un corte más.'
        ) : (
          <>
            Te quedarán <strong className="font-semibold">{calculo.quedan}</strong>{' '}
            {calculo.quedan === 1 ? 'mensualidad' : 'mensualidades'} por pagar ·{' '}
            <strong className="font-semibold">
              {formatMoney(calculo.faltaCents, { hideZeroCents: true })}
            </strong>
          </>
        )}
      </p>

      {/* El residuo de MSI: los bancos mexicanos lo cobran en la última. Decirlo
          antes evita la llamada de «me cobraron un centavo de más». */}
      {sinIntereses && calculo.ultimaCents !== calculo.mensualidadCents ? (
        <p data-money className="mt-1.5 text-[12px] leading-snug text-ink-soft">
          La última será de {formatMoney(calculo.ultimaCents)}: el precio no se parte exacto entre{' '}
          {meses}, y ese resto lo cobra el banco al final.
        </p>
      ) : null}

      {!sinIntereses ? (
        <p data-money className="mt-1.5 text-[12px] leading-snug text-expense-text">
          Pagarás {formatMoney(calculo.interesesCents, { hideZeroCents: true })} de intereses
          {pctInteres > 0 ? ` — un ${pctInteres}% sobre el precio` : ''}.
          {/* El total solo se dice cuando no es el mismo número que lo que
              falta. En una compra nueva son idénticos, y repetir la cifra dos
              renglones seguidos hace dudar de si son cosas distintas. */}
          {calculo.totalAPagarCents !== calculo.faltaCents
            ? ` En total, ${formatMoney(calculo.totalAPagarCents, { hideZeroCents: true })}.`
            : ''}
        </p>
      ) : null}

      {calculo.fueraDeLibros > 0 ? (
        <p className="mt-2 border-t border-[var(--border)] pt-2 text-[12px] leading-snug text-ink-soft">
          {calculo.fueraDeLibros === 1
            ? 'Una mensualidad cayó en un corte que ya había cerrado cuando registraste la compra, así que PayBae no la cobra: esa ya la pagaste con tu tarjeta.'
            : `${calculo.fueraDeLibros} mensualidades cayeron en cortes que ya habían cerrado cuando registraste la compra, así que PayBae no las cobra: esas ya las pagaste con tu tarjeta.`}
        </p>
      ) : null}
    </div>
  )
}

/**
 * La cuenta, con el motor compartido.
 *
 * `firstChargeableNumber` es el que sabe la parte difícil: cuántas mensualidades
 * quedaron fuera por caer en cortes anteriores al día en que la compra entró en
 * los libros. Sin él, registrar hoy una compra de mayo con «0 pagadas» diría que
 * faltan doce cuando el servidor solo va a cobrar nueve.
 */
function calcular(input: {
  montoCents: number
  meses: number
  sinIntereses: boolean
  mensualidadCents: number
  pagadas: number
  compradaEl: string
  anclaContable: string
  tarjeta: CreditCard | null
}): Calculo | null {
  const { montoCents, meses, sinIntereses, mensualidadCents, compradaEl, tarjeta } = input
  const pagadas = Number.isInteger(input.pagadas) ? input.pagadas : 0

  if (montoCents <= 0) return null
  if (!Number.isInteger(meses) || meses < 2 || meses > 60) return null
  if (!sinIntereses && mensualidadCents <= 0) return null
  // Una mensualidad que no llega a cubrir el precio no describe ningún plan
  // real: el servidor la rechaza, y calcular con ella pintaría una compra de
  // $45,000 que se paga con $1,800. Mejor no enseñar cifra que enseñar esa.
  if (!sinIntereses && mensualidadCents * meses < montoCents) return null
  if (pagadas < 0 || pagadas > meses) return null

  const montos = installmentAmounts(montoCents, meses, sinIntereses ? undefined : mensualidadCents)
  const totalAPagarCents = montos.reduce((s, m) => s + m, 0)

  /**
   * Desde qué mensualidad cobra la app. Sin tarjeta elegida todavía no se puede
   * ubicar el corte, así que se cae a lo único que sí se sabe —lo que la persona
   * declaró como pagado—; en cuanto elige tarjeta, la cifra se corrige sola.
   */
  let desde = pagadas + 1
  if (tarjeta && isPlainDate(compradaEl) && isPlainDate(input.anclaContable)) {
    const plan: PlanCalculable = {
      totalAmountCents: montoCents,
      months: meses,
      monthlyAmountCents: sinIntereses ? montos[0]! : mensualidadCents,
      monthsPaid: pagadas,
      purchasedOn: compradaEl as PlainDate,
      bookedOn: input.anclaContable as PlainDate,
      interestFree: sinIntereses,
    }
    const config: CardCycleConfig = {
      statementDay: tarjeta.statementDay,
      paymentTermDays: tarjeta.paymentTermDays,
      paymentTermType: tarjeta.paymentTermType,
      cutoffInclusive: tarjeta.cutoffInclusive,
    }
    // Los festivos no entran: `firstChargeableNumber` solo compara fechas de
    // corte, que son de calendario. Los días hábiles solo mueven vencimientos, y
    // esos los pinta el calendario del detalle con los datos del servidor.
    desde = firstChargeableNumber(plan, config)
  }

  const quedan = Math.max(0, meses - desde + 1)
  return {
    mensualidadCents: montos[0]!,
    ultimaCents: montos[meses - 1]!,
    quedan,
    faltaCents: montos.slice(desde - 1).reduce((s, m) => s + m, 0),
    totalAPagarCents,
    interesesCents: Math.max(0, totalAPagarCents - montoCents),
    fueraDeLibros: Math.max(0, desde - 1 - pagadas),
  }
}

/* ── Opciones ───────────────────────────────────────────────────────────── */

function opcionesCategoria(categorias: readonly Category[]): ReadonlyArray<OpcionSegmento<string>> {
  const vivas = categorias
    .filter((c) => !c.archivedAt && c.group !== 'INCOME')
    // `sort` es estable: dentro de cada grupo se conserva el orden del servidor.
    .sort((a, b) => RANGO_GRUPO[a.group] - RANGO_GRUPO[b.group])
  return [
    { value: '', label: 'Sin categoría', detalle: 'La defines después' },
    ...vivas.map((c) => ({ value: c.id, label: c.name })),
  ]
}

function opcionesTarjeta(tarjetas: readonly CreditCard[]): ReadonlyArray<OpcionSegmento<string>> {
  return tarjetas.map((t) => ({
    value: t.id,
    label: `${t.name}${t.last4 ? ` ·· ${t.last4}` : ''}`,
    detalle: `Corta el ${t.statementDay} de cada mes`,
  }))
}

/**
 * A quién se le atribuye el gasto.
 *
 * Un rol limitado solo puede registrar lo suyo: el fondo común y lo ajeno los
 * rechaza el servidor. Se deja únicamente su opción en vez de ofrecer casillas
 * que fallan al guardar.
 */
function opcionesIntegrante(
  integrantes: ReadonlyArray<{ id: string; user: { displayName: string } }>,
  soloMiembroId: string | null,
): ReadonlyArray<OpcionSegmento<string>> {
  const visibles =
    soloMiembroId === null ? integrantes : integrantes.filter((m) => m.id === soloMiembroId)
  const personas = visibles.map((m) => ({
    value: m.id,
    label: m.user.displayName.split(' ')[0] ?? m.user.displayName,
    detalle: 'Gasto personal',
  }))
  if (soloMiembroId !== null) return personas
  return [{ value: '', label: 'Común', detalle: 'Fondo del hogar' }, ...personas]
}

/** Dice qué falta, en vez de un botón muerto sin explicación. */
function queFalta(errores: Record<string, string | undefined>): string {
  if (errores.nombre) return 'Ponle nombre a la compra'
  if (errores.monto) return 'Escribe cuánto costó'
  if (errores.tarjeta) return 'Elige la tarjeta'
  if (errores.meses) return 'Elige el plazo'
  if (errores.mensualidad) return 'Revisa la mensualidad'
  if (errores.pagadas) return 'Revisa las que ya pagaste'
  if (errores.fecha) return 'Revisa la fecha de compra'
  return 'Guardar'
}
